import { useReducer, useCallback } from "react";

interface BatchState {
  selectedIds: Set<string>;
  focusedIndex: number;
}

type BatchAction =
  | { type: "toggle"; id: string }
  | { type: "toggle_all"; ids: string[] }
  | { type: "clear" }
  | { type: "set_focused_index"; index: number }
  | { type: "move_focus"; direction: "next" | "prev"; total: number };

function batchReducer(state: BatchState, action: BatchAction): BatchState {
  switch (action.type) {
    case "toggle": {
      const next = new Set(state.selectedIds);
      if (next.has(action.id)) {
        next.delete(action.id);
      } else {
        next.add(action.id);
      }
      return { ...state, selectedIds: next };
    }
    case "toggle_all": {
      const allSelected =
        action.ids.length > 0 &&
        action.ids.every((id) => state.selectedIds.has(id));
      if (allSelected) {
        const next = new Set(state.selectedIds);
        for (const id of action.ids) next.delete(id);
        return { ...state, selectedIds: next };
      }
      const next = new Set(state.selectedIds);
      for (const id of action.ids) next.add(id);
      return { ...state, selectedIds: next };
    }
    case "clear": {
      return { ...state, selectedIds: new Set() };
    }
    case "set_focused_index": {
      return { ...state, focusedIndex: action.index };
    }
    case "move_focus": {
      if (action.total === 0) return state;
      const next =
        action.direction === "next"
          ? state.focusedIndex < action.total - 1
            ? state.focusedIndex + 1
            : 0
          : state.focusedIndex > 0
            ? state.focusedIndex - 1
            : action.total - 1;
      return { ...state, focusedIndex: next };
    }
    default:
      return state;
  }
}

export function useBatchSelection() {
  const [state, dispatch] = useReducer(batchReducer, {
    selectedIds: new Set<string>(),
    focusedIndex: -1,
  });

  const toggle = useCallback((id: string) => {
    dispatch({ type: "toggle", id });
  }, []);

  const toggleAll = useCallback((ids: string[]) => {
    dispatch({ type: "toggle_all", ids });
  }, []);

  const clear = useCallback(() => {
    dispatch({ type: "clear" });
  }, []);

  const setFocusedIndex = useCallback((index: number) => {
    dispatch({ type: "set_focused_index", index });
  }, []);

  const moveFocus = useCallback((direction: "next" | "prev", total: number) => {
    dispatch({ type: "move_focus", direction, total });
  }, []);

  return {
    selectedIds: state.selectedIds,
    focusedIndex: state.focusedIndex,
    toggle,
    toggleAll,
    clear,
    setFocusedIndex,
    moveFocus,
  };
}
